import { api } from '@/lib/api';
import type { Equipment } from './types';
import type { EquipmentCreateValues, EquipmentUpdateValues } from './schema';

/**
 * Equipment REST client.
 *
 * Endpoints (Phase 3a):
 *   GET    /equipment              list (optionally filter by ?vesselId=)
 *   GET    /equipment/tree/:vesselId   hierarchical tree for one vessel
 *   GET    /equipment/:id          detail
 *   POST   /equipment              create
 *   PATCH  /equipment/:id          update (any subset of editable fields)
 *   DELETE /equipment/:id          soft-delete
 *
 * The tree endpoint returns the same Equipment shape with an extra
 * `children: Equipment[]` recursive field — the type lives next to
 * the hook that consumes it (F4.3).
 */

export interface EquipmentTreeNode extends Equipment {
  /** Always returned by the current backend; declared optional so
   *  the recursive renderer stays crash-safe when an older backend
   *  (or a partial reload from offline cache) omits the array. */
  children?: EquipmentTreeNode[];
}

interface ListParams {
  vesselId?: string;
}

export const equipmentApi = {
  list: async (params: ListParams = {}): Promise<Equipment[]> => {
    const { data } = await api.get<Equipment[]>('/equipment', { params });
    return data;
  },

  tree: async (vesselId: string): Promise<EquipmentTreeNode[]> => {
    const { data } = await api.get<EquipmentTreeNode[]>(
      `/equipment/tree/${vesselId}`,
    );
    return data;
  },

  getById: async (id: string): Promise<Equipment> => {
    const { data } = await api.get<Equipment>(`/equipment/${id}`);
    return data;
  },

  create: async (input: EquipmentCreateValues): Promise<Equipment> => {
    const { data } = await api.post<Equipment>('/equipment', input);
    return data;
  },

  update: async (
    id: string,
    patch: EquipmentUpdateValues,
  ): Promise<Equipment> => {
    const { data } = await api.patch<Equipment>(`/equipment/${id}`, patch);
    return data;
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/equipment/${id}`);
  },
};
